import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { BsModalRef } from 'ngx-bootstrap/modal'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/core/core.state'
import { Question } from '../../survey/question/question'
import { CourseService } from '../course.service'
import { NotificationService } from 'src/app/core/notification.service'

interface AchievementQuestionRow {
  questionId: number
  questionLabel: string
  currentRating: number | null
  draftRating: number | null
  isEditing: boolean
  wasAdjusted: boolean
}

interface BulkMemberAnswerRow {
  memberId: number
  memberName: string
  answersByQuestionId: Record<number, string | null>
}

type AchievementBulkMode = 'selected' | 'all'

@UntilDestroy()
@Component({
  templateUrl: './adjust-answers-modal.component.html',
  styleUrls: ['./adjust-answers-modal.component.scss']
})
export class AdjustAchievementAnswersModalComponent implements OnInit {
  @Input() courseId: number
  @Input() memberId: number
  @Input() memberName: string
  @Input() questions: Question[] = []
  @Input() answersByQuestionId: Record<number, string | null> = {}
  @Input() isBulkMode: boolean = false
  @Input() bulkMode: AchievementBulkMode = 'selected'
  @Input() bulkMembers: BulkMemberAnswerRow[] = []
  @Output() saved = new EventEmitter<void>()

  public rows: AchievementQuestionRow[] = []
  public stars: number[] = [0, 1, 2]
  public isSaving: boolean = false
  public overwriteExisting: boolean = true
  private appLanguage: string = 'en'

  constructor(
    private bsModalRef: BsModalRef,
    private store: Store<AppState>,
    private courseService: CourseService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.store
      .select(settingSelectors.selectById, 'language')
      .pipe(untilDestroyed(this))
      .subscribe((language) => {
        this.appLanguage = language || 'en'
        this.buildRows()
      })
  }

  public close(): void {
    this.bsModalRef.hide()
  }

  public enableEdit(row: AchievementQuestionRow): void {
    row.isEditing = true
  }

  public selectStar(row: AchievementQuestionRow, selected: number): void {
    if (!row.isEditing) {
      return
    }

    row.draftRating = selected + 1
    row.wasAdjusted = true
  }

  public getDisplayRating(row: AchievementQuestionRow): number {
    if (row.draftRating === null || row.draftRating === undefined) {
      return 0
    }

    return row.draftRating
  }

  public save(): void {
    if (!this.hasChanges()) {
      return
    }

    if (this.isBulkMode) {
      this.saveBulk()
      return
    }

    const ratings = this.buildMergedRatingsPayload()

    this.isSaving = true
    this.courseService
      .updateParticipantAchievements(this.courseId, this.memberId, ratings)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.notificationService.emit('alerts.success', 'success')
          this.saved.emit()
          this.bsModalRef.hide()
        },
        error: () => {
          this.notificationService.emit('alerts.error', 'error')
          this.isSaving = false
        }
      })
  }

  public hasChanges(): boolean {
    if (this.isBulkMode) {
      return this.rows.some((row) => row.wasAdjusted)
    }

    return this.rows.some((row) => row.draftRating !== row.currentRating)
  }

  public onToggleOverwrite(event: Event): void {
    const target = event.target as HTMLInputElement
    this.overwriteExisting = !!target?.checked
  }

  public getBulkTitleKey(): string {
    return this.bulkMode === 'all'
      ? 'achievements.setAllToAchieved'
      : 'achievements.setAchievements'
  }

  public getBulkMessageKey(): string {
    return this.bulkMode === 'all'
      ? 'achievements.bulkAdjustAllWarning'
      : 'achievements.bulkAdjustSelectedMessage'
  }

  public getSelectedMemberNames(): string {
    return this.bulkMembers
      .map((member) => member.memberName)
      .filter(Boolean)
      .join(', ')
  }

  private saveBulk(): void {
    const editedRows = this.rows.filter((row) => row.wasAdjusted)
    if (!editedRows.length) {
      return
    }

    const updates = this.bulkMembers
      ?.map((member) => {
        const mergedRatingsByQuestionId = new Map<number, number>()

        this.rows.forEach((row) => {
          const rawValue = member.answersByQuestionId?.[row.questionId]
          const existingRating = rawValue != null ? Number(rawValue) : null

          if (existingRating !== null) {
            mergedRatingsByQuestionId.set(row.questionId, existingRating)
          }
        })

        editedRows.forEach((row) => {
          if (
            this.overwriteExisting ||
            !mergedRatingsByQuestionId.has(row.questionId)
          ) {
            mergedRatingsByQuestionId.set(row.questionId, row.draftRating)
          }
        })

        const ratings = Array.from(mergedRatingsByQuestionId.entries()).map(
          ([questionId, result]) => ({
            questionId,
            result
          })
        )

        return ratings.length
          ? {
              personId: member.memberId,
              ratings
            }
          : null
      })
      .filter(Boolean)

    if (!updates.length) {
      this.saved.emit()
      this.bsModalRef.hide()
      return
    }

    this.isSaving = true
    this.courseService
      .updateParticipantsAchievementsBulk(this.courseId, updates)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.notificationService.emit('alerts.success', 'success')
          this.saved.emit()
          this.bsModalRef.hide()
        },
        error: () => {
          this.notificationService.emit('alerts.error', 'error')
          this.isSaving = false
        }
      })
  }

  private buildMergedRatingsPayload(): {
    questionId: number
    result: number
  }[] {
    return this.rows.reduce(
      (acc, row) => {
        const hasExisting = row.currentRating !== null
        const wasEdited = row.draftRating !== row.currentRating

        if (!hasExisting && !wasEdited) {
          return acc
        }

        acc.push({
          questionId: row.questionId,
          result: row.draftRating ?? 1
        })

        return acc
      },
      [] as { questionId: number; result: number }[]
    )
  }

  private getQuestionLabel(question: Question): string {
    return (
      question?.body?.find((row) => row.language === this.appLanguage)?.text ||
      question?.body?.[0]?.text ||
      ''
    )
  }

  private buildRows(): void {
    this.rows = this.questions?.map((question) => {
      const rawValue = this.answersByQuestionId?.[question.id]
      const currentRating =
        !this.isBulkMode && rawValue != null ? Number(rawValue) : null

      return {
        questionId: question.id,
        questionLabel: this.getQuestionLabel(question),
        currentRating,
        draftRating: currentRating,
        isEditing: false,
        wasAdjusted: false
      }
    })
  }
}
